import {
  alpha,
  createStyles,
  makeStyles,
  type Theme,
} from '@material-ui/core/styles';
import { artifactJourneyStyles } from './artifactJourneyStyles';
import { cockpitType } from './cockpitTokens';
import { readerStyles } from './readerStyles';
import { evidenceStyles } from './evidenceStyles';
import { timelineStyles } from './timelineStyles';

const shellStyles = (theme: Theme) =>
  createStyles({
    shell: { display: 'grid', gap: theme.spacing(2.5) },
    panel: { padding: theme.spacing(3) },
    // the coverage panel carries the page's claim, so it is the only one that
    // states its allegiance in the frame itself
    panelLead: {
      padding: theme.spacing(3),
      borderTop: `3px solid ${theme.palette.secondary.main}`,
    },
    sectionTitle: { ...cockpitType.sectionTitle },
    // one rule under every section title, so the sections read as siblings
    sectionHead: {
      display: 'grid',
      gap: theme.spacing(0.5),
      paddingBottom: theme.spacing(1.5),
      marginBottom: theme.spacing(2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    sectionLede: { color: theme.palette.text.secondary },
    summary: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    // Backstage's header does not constrain this row, so without an explicit
    // wrap the last action leaves the viewport instead of moving to a new line
    headerActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'flex-end',
      maxWidth: '100%',
      minWidth: 0,
      // narrow enough for the subtitle to wrap, the actions land on the header's
      // second flex line, where its negative grid margin pulls them over the
      // subtitle; the offset clears it and left-aligns them under the title
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
        justifyContent: 'flex-start',
      },
    },
    headerChip: {
      color: 'inherit',
      borderColor: 'currentColor',
    },
    sectionIntro: { marginBottom: theme.spacing(2) },
  });

// A row of measurements is one object. Boxing each cell tripled the border
// count without adding a single fact, so the cells are divided, not framed.
const metricStyles = (theme: Theme) =>
  createStyles({
    metricGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      columnGap: theme.spacing(3),
      rowGap: theme.spacing(3),
    },
    // whitespace separates the columns; the numerals are heavy enough to hold
    // them without rules, and the first value stays flush with the section title
    metric: {
      display: 'grid',
      gap: theme.spacing(0.75),
      alignContent: 'start',
      minWidth: 0,
    },
    metricLabel: {
      ...cockpitType.eyebrow,
      color: theme.palette.text.secondary,
      // two lines are reserved so a label that wraps does not drop its own
      // numeral below the rest of the row
      minHeight: `${cockpitType.eyebrow.lineHeight * 2}em`,
    },
    metricValueRow: {
      display: 'flex',
      gap: theme.spacing(0.75),
      alignItems: 'baseline',
      flexWrap: 'wrap',
      minWidth: 0,
    },
    metricValue: {
      ...cockpitType.numeral,
      overflowWrap: 'anywhere',
    },
    metricUnit: {
      color: theme.palette.text.secondary,
      overflowWrap: 'anywhere',
    },
  });

const metricNoticeStyles = (theme: Theme) =>
  createStyles({
    contextualNotice: {
      borderLeft: `3px solid ${theme.palette.warning.main}`,
      background: alpha(theme.palette.warning.main, 0.07),
      borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
      overflowWrap: 'anywhere',
      padding: theme.spacing(1, 1.5),
    },
    contextualNoticeError: {
      borderLeftColor: theme.palette.error.main,
      background: alpha(theme.palette.error.main, 0.07),
    },
    metricStatus: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.75),
      margin: theme.spacing(1, 0),
    },
    progressiveDetails: {
      marginTop: theme.spacing(1),
      '& summary': { cursor: 'pointer', fontWeight: 600 },
      '& summary:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: 2,
      },
    },
  });

// Material-UI v4 generates suffixed class names, so the summary's built-in
// spacing can only be reached through the `classes` prop, not a descendant
// selector. `metricsExpanded` is the empty rule those overrides key off.
const metricsDisclosureStyles = (theme: Theme) =>
  createStyles({
    metricsSummary: {
      padding: 0,
      minHeight: 0,
      // the disclosure control sits on the same rule as every other title
      paddingBottom: theme.spacing(1.5),
      '&$metricsExpanded': { minHeight: 0 },
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: 2,
      },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    },
    metricsSummaryContent: {
      margin: 0,
      minWidth: 0,
      overflowWrap: 'anywhere',
      '&$metricsExpanded': { margin: 0 },
    },
    metricsExpanded: {},
    // the same rule the other sections draw under their heading, so the
    // collapsible one still reads as a sibling
    metricsDetails: {
      display: 'block',
      padding: theme.spacing(2.5, 0, 0),
      borderTop: `1px solid ${theme.palette.divider}`,
      '& > div': { width: '100%', minWidth: 0 },
    },
  });

const guaranteeStyles = (theme: Theme) =>
  createStyles({
    guaranteeImpact: { marginTop: theme.spacing(1) },
    guaranteeGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: theme.spacing(1),
    },
    controlCard: {
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'grid',
      gap: theme.spacing(0.75),
    },
  });

const sessionObservabilityStyles = (theme: Theme) =>
  createStyles({
    sessionPanel: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'grid',
      gap: theme.spacing(1.5),
    },
    sessionCapabilityGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: theme.spacing(1),
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    sessionCapability: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      display: 'grid',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
    sessionJointDiagnostic: {
      borderLeft: `${theme.spacing(0.5)}px solid ${theme.palette.warning.main}`,
      display: 'grid',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
  });

export const useCockpitStyles = makeStyles(theme =>
  createStyles({
    ...shellStyles(theme),
    ...evidenceStyles(theme),
    ...artifactJourneyStyles(theme),
    ...metricStyles(theme),
    ...metricNoticeStyles(theme),
    ...metricsDisclosureStyles(theme),
    ...guaranteeStyles(theme),
    ...sessionObservabilityStyles(theme),
    ...timelineStyles(theme),
    ...readerStyles(theme),
  }),
);
